import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentAttachments = ({ documents, onAddDocument, onRemoveDocument, canEdit }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files);
    files?.forEach(file => {
      onAddDocument({
        id: Date.now() + Math.random(),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadedBy: 'Current User',
        uploadedDate: new Date()?.toISOString()
      });
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    files?.forEach(file => {
      onAddDocument({
        id: Date.now() + Math.random(),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadedBy: 'Current User',
        uploadedDate: new Date()?.toISOString()
      });
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return 'FileText';
    if (type?.includes('word') || type?.includes('document')) return 'FileText';
    if (type?.includes('excel') || type?.includes('spreadsheet')) return 'Table';
    if (type?.includes('image')) return 'Image';
    return 'File';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Paperclip" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Attachments ({documents?.length})</h3>
        </div>
        {canEdit && (
          <label>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            />
            <Button
              variant="outline"
              size="sm"
              iconName="Upload"
              iconPosition="left"
              asChild
            >
              <span>Upload</span>
            </Button>
          </label>
        )}
      </div>
      {canEdit && (
        <div
          className={`mb-4 p-6 border-2 border-dashed rounded-lg text-center transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5' :'border-border bg-muted/20 hover:border-primary/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-1">
            Drag and drop files here
          </p>
          <p className="text-xs text-muted-foreground">
            or click Upload button above
          </p>
        </div>
      )}
      <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
        {documents?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Paperclip" size={48} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No attachments yet</p>
          </div>
        ) : (
          documents?.map((doc) => (
            <div
              key={doc?.id}
              className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors group"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={getFileIcon(doc?.type)} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{doc?.name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatFileSize(doc?.size)}</span>
                    <span>•</span>
                    <span>{formatDate(doc?.uploadedDate)}</span>
                    <span>•</span>
                    <span>{doc?.uploadedBy}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  aria-label="Download document"
                >
                  <Icon name="Download" size={16} />
                </button>
                {canEdit && (
                  <button
                    onClick={() => onRemoveDocument(doc?.id)}
                    className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove document"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentAttachments;